import { FC } from 'react';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { constants } from 'src/app/common/constants';
import PruTable from 'src/app/common/components/PruTable/PruTable';
import { KeyTaskRecord, PublishedStatusEnum } from '../../../../types/key-task-summarizer-type';
import { getCategoryTranslationKey, isRecordValid } from '../../../../utils/key-task-summarizer-utils';

const formatDateTime = (value: Date | null): string => {
  if (!value) return '';
  const date = moment(value);
  return date.isValid() ? date.format(constants.Date_Minute_Scale_Format) : '';
};

type KeyTaskSummarizerListProps = {
  isLoading: boolean;
  keyTaskList: KeyTaskRecord[];
  totalRecords: number;
  page: number;
  limit: number;
  enableCreate: boolean;
  enableUpdate: boolean;
  noRecordMsg: string;
  onRefresh: () => void;
  onChangePage: (page: number, rowsPerPage: number) => void;
  onAdd: () => void;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onUnpublish: (row: KeyTaskRecord) => void;
};

const KeyTaskSummarizerList: FC<KeyTaskSummarizerListProps> = ({
  isLoading,
  keyTaskList,
  totalRecords,
  page,
  limit,
  enableCreate,
  enableUpdate,
  noRecordMsg,
  onRefresh,
  onChangePage,
  onAdd,
  onView,
  onEdit,
  onUnpublish,
}) => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });

  return (
    <PruTable<KeyTaskRecord>
      key={`${page}-${limit}`}
      title={Translation('KeyTaskSummerizer.title')}
      disableBulkSelect
      headerBtnDef={[
        {
          color: 'primary',
          title: Translation('app.button.add'),
          onClick: onAdd,
          condition: () => !!enableCreate,
        },
      ]}
      operationSticky={true}
      operationDef={[
        {
          title: Translation('app.button.view'),
          tooltipText: Translation('app.button.view'),
          onClick: (row) => onView(row.id),
        },
        {
          title: Translation('app.button.edit'),
          tooltipText: Translation('app.button.edit'),
          onClick: (row) => onEdit(row.id),
          condition: () => !!enableUpdate,
        },
        {
          title: Translation('app.button.unpublish'),
          tooltipText: Translation('app.button.unpublish'),
          onClick: onUnpublish,
          condition: (row) => row.publishedStatus === PublishedStatusEnum.PUBLISHED && !!enableUpdate && !isLoading,
        },
      ]}
      columnDef={[
        {
          isId: true,
          keyIndex: 'id',
          hidden: true,
          displayName: '',
          renderData: () => '',
        },
        {
          keyIndex: 'category',
          align: 'left',
          displayName: Translation('KeyTaskSummerizer.category'),
          renderData: (row) => Translation(getCategoryTranslationKey(row.category)),
        },
        {
          keyIndex: 'contentEn',
          align: 'left',
          displayName: Translation('KeyTaskSummerizer.contentEn'),
          style: { whiteSpace: 'pre-wrap' },
          renderData: (row) => row.contentEn,
        },
        {
          keyIndex: 'contentZhHk',
          align: 'left',
          displayName: Translation('KeyTaskSummerizer.contentZhHk'),
          style: { whiteSpace: 'pre-wrap' },
          renderData: (row) => row.contentZhHk,
        },
        {
          keyIndex: 'effectiveStartDate',
          align: 'left',
          displayName: Translation('component.formLabel.effective-date-range-start'),
          renderData: (row) => formatDateTime(row.effectiveStartDate),
        },
        {
          keyIndex: 'effectiveEndDate',
          align: 'left',
          displayName: Translation('component.formLabel.effective-date-range-end'),
          renderData: (row) => formatDateTime(row.effectiveEndDate),
        },
        {
          keyIndex: 'publishedStatus',
          align: 'left',
          displayName: Translation('component.formLabel.publish-status'),
          renderData: (row) => Translation(`component.status.${row.publishedStatus}`),
        },
        {
          keyIndex: 'validStatus',
          align: 'left',
          displayName: Translation('component.formLabel.valid-status'),
          renderData: (row) => Translation(isRecordValid(row) ? 'component.status.valid' : 'component.status.invalid'),
        },
        {
          keyIndex: 'order',
          align: 'left',
          displayName: Translation('KeyTaskSummerizer.display_order'),
          renderData: (row) => String(row.order),
        },
      ]}
      defaultPageNumber={page - 1}
      defaultRowsPerPage={limit}
      isLoading={isLoading}
      disableRefresh={isLoading}
      onRefresh={onRefresh}
      dataSource={keyTaskList}
      totalRecords={totalRecords}
      noRecordMsg={noRecordMsg}
      onChangePage={onChangePage}
    />
  );
};

export default KeyTaskSummarizerList;
